import { Model, Factory, trait, type ModelInstance } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randFirstName,
  randLastName,
  randCompanyName,
  randRecentDate,
  randNumber,
  randSentence,
  randPhoneNumber,
  randEmail,
  randAddress,
  randChanceBoolean,
  randCatchPhrase,
  randBetweenDate,
} from '@ngneat/falso'

import type { PhoneData } from '@/models/client.js'
import type { SchemaAttributes } from '../types.js'
import { pageable } from './utils.js'

import type { MirageServer, MockServer } from './server.js'
import {
  generatePatientSpeciesIcon,
  type PatientDataWithRelations,
} from './Patient.js'
import type { ClientDataWithRelations } from './Client.js'
import { getInitials } from '@/utils/string.js'
import {
  clientDetailsUrl,
  consultationDetailsUrl,
  diagnosticImagingReferralDetailsUrl,
  estimateDetailsUrl,
  invoiceDetailsUrl,
  laboratoryReferralDetailsUrl,
  patientDetailsUrl,
} from '@/utils/url.js'

type UserViewHistoryData = SchemaAttributes<'UserViewHistory'>

export const model = Model as ModelDefinition<UserViewHistoryData>

export const factory = Factory.extend<UserViewHistoryData>({
  content_type: 'CLIENT',
  object_id(index: number) {
    return (index + 1).toString()
  },
  url() {
    return '#'
  },
  client_id(index: number) {
    return (index + 1).toString()
  },
  client_title() {
    return rand([null, rand(['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'])])
  },
  client_organization_name() {
    return rand([null, randCompanyName()])
  },
  client_first_name() {
    return randFirstName()
  },
  client_last_name() {
    return randLastName()
  },
  client_initials() {
    return rand([
      null,
      (this.client_first_name as string).charAt(0) +
        (this.client_last_name as string).charAt(0),
    ])
  },
  client_phone_number() {
    return rand([null, randPhoneNumber()])
  },
  client_email() {
    return rand([null, randEmail()])
  },
  client_address() {
    return rand([null, randAddress().toString()])
  },
  patient_id: null,
  patient_name: null,
  patient_species: null,
  patient_species_icon: null,
  patient_archived: null,
  patient_gender: null,
  patient_breed: null,
  patient_birth_date: null,
  patient_deceased_date: null,
  patient_weight: null,
  patient_microchip: null,
  patient_passport_number: null,
  patients_count: null,
  consultation_reason: null,
  estimate_title: null,
  estimate_total_amount: null,
  invoice_total: null,
  referral_patients_count: null,
  referral_analyses: null,
  referral_target_areas: null,
  last_viewed() {
    return randRecentDate().toISOString()
  },
  async afterCreate(clientView, server) {
    // By default, set Client Data
    let client = (await server.schema.where('client', {
      id: clientView.object_id,
    }).models[0]) as ModelInstance<ClientDataWithRelations> | null
    if (!client) {
      client = (await server.create('client', 'withPhoneNumbers', {
        id: clientView.object_id,
      })) as ModelInstance<ClientDataWithRelations>
    }

    clientView.update({
      // BE returns `client_id` value as `null` when of type Client.
      // This value is only meant for Items of type Consultation, Invoice, Estimate, etc.
      client_id: null,
      // The Client's ID is stored in `object_id` instead.
      object_id: client.id,
      url: clientDetailsUrl({
        provetId: 'PROVET_ID',
        clientId: client.id!,
        legacyUrl: true,
      }).toString(),

      client_title: client.title,
      client_organization_name: client.organization_name,
      client_first_name: client.first_name,
      client_last_name: client.last_name,
      client_initials: getInitials([client.first_name, client.last_name]),
      client_phone_number: client.phoneNumbers?.models
        ?.map((phone: ModelInstance<PhoneData>) => phone.phone_number)
        ?.filter(Boolean)?.[0],
      client_email: client.email,
      client_address: [
        client.street_address,
        client.street_address_2,
        client.street_address_3,
        client.city,
        client.zip_code,
        client.state,
        client.country,
      ]
        ?.filter(Boolean)
        ?.join(', '),
      client_archived: client.archived,
    })
  },

  isPatient: trait({
    content_type: 'PATIENT',

    async afterCreate(patientView, server) {
      // Get Patient full data
      let patient = (await server.schema.where('patient', {
        id: patientView.object_id,
      }).models[0]) as ModelInstance<PatientDataWithRelations> | null

      // If no Patient with that ID exists, create it and use it
      if (!patient) {
        patient = (await server.create('patient', {
          id: patientView.object_id,
        })) as ModelInstance<PatientDataWithRelations>
      }

      const clientId = patient?.client?.id
      let clientData
      // If Client with that ID exists, use it
      if (clientId) {
        clientData = await server.schema.where('client', {
          id: clientId,
        }).models[0]
      }
      // If no Client with that ID exists, create it and use it
      if (!clientId || !clientData) {
        clientData = await server.create('client', 'withPhoneNumbers', {
          id: clientId,
        })
      }

      // Update Patient DB with Client Data
      await patient.update({
        client: clientData,
      })

      // Update Patient with Client Data
      await patientView.update({
        // BE returns `patient_id` value as `null` when of type Patient.
        // This value is only meant for Items of type Consultation, Invoice, Estimate, etc.
        patient_id: null,
        // The Patient's ID is stored in `object_id` instead.
        object_id: patient.id,

        // Patient URL
        url: patientDetailsUrl({
          provetId: 'PROVET_ID',
          clientId: clientData.id!,
          patientId: patient.id!,
        }).toString(),

        // Patient Data
        patient_name: patient.name,
        patient_species: patient.species,
        patient_species_icon: generatePatientSpeciesIcon(
          patient.species as string,
        ),
        patient_archived: patient.archived,
        patient_gender: patient.gender,
        patient_breed: patient.breed,
        patient_birth_date: patient.date_of_birth,
        patient_deceased_date:
          patient.date_of_birth && randChanceBoolean({ chanceTrue: 0.05 })
            ? randBetweenDate({
                from: patient.date_of_birth,
                to: randRecentDate(),
              }).toISOString()
            : rand([null, undefined]),
        patient_weight: patient.weight,
        patient_microchip: patient.microchip,
        patient_passport_number: null,

        // Client Data
        client_id: clientData?.id,
        client_title: clientData?.title,
        client_organization_name: clientData?.organization_name,
        client_first_name: clientData?.first_name,
        client_last_name: clientData?.last_name,
        client_initials: getInitials([
          clientData?.first_name,
          clientData?.last_name,
        ]),
        client_phone_number: clientData.phoneNumbers?.models
          ?.map((phone: ModelInstance<PhoneData>) => phone.phone_number)
          ?.filter(Boolean)?.[0],
        client_email: clientData?.email,
        client_address: [
          clientData.street_address,
          clientData.street_address_2,
          clientData.street_address_3,
          clientData.city,
          clientData.zip_code,
          clientData.state,
          clientData.country,
        ]
          ?.filter(Boolean)
          ?.join(', '),
        client_archived: clientData.archived,
      })
    },
  }),

  isConsultation: trait({
    content_type: 'CONSULTATION',
    client_id(index: number) {
      return (index + 1).toString()
    },
    patient_id(index: number) {
      return (index + 1).toString()
    },
    patient_name() {
      return randFirstName()
    },
    patient_species: 'Dog',
    patients_count() {
      return randNumber({ min: 0, max: 3 })
    },
    consultation_reason() {
      return randSentence()
    },
    afterCreate(consultationView) {
      consultationView.update({
        url: consultationDetailsUrl({
          provetId: 'PROVET_ID',
          consultationId: consultationView.object_id,
        }).toString(),
      })
    },
  }),

  isEstimate: trait({
    content_type: 'ESTIMATE',
    client_id(index: number) {
      return (index + 1).toString()
    },
    estimate_title() {
      return randCatchPhrase()
    },
    estimate_total_amount() {
      const amount = randNumber({ min: 300, max: 33300 }) / 100
      return `€${amount.toFixed(2)}`
    },
    afterCreate(estimateView) {
      estimateView.update({
        url: estimateDetailsUrl({
          provetId: 'PROVET_ID',
          estimateId: estimateView.object_id,
        }).toString(),
      })
    },
  }),

  isEstimateWithoutClient: trait({
    content_type: 'ESTIMATE',
    estimate_title() {
      return randCatchPhrase()
    },
    estimate_total_amount() {
      const amount = randNumber({ min: 300, max: 33300 }) / 100
      return `€${amount.toFixed(2)}`
    },

    // Remove Client attributes from Patient
    client_title: null,
    client_organization_name: null,
    client_first_name: null,
    client_last_name: null,
    client_initials: null,
    client_phone_number: null,
    client_email: null,
    client_address: null,
    client_archived: null,
    afterCreate(estimateView) {
      estimateView.update({
        url: estimateDetailsUrl({
          provetId: 'PROVET_ID',
          estimateId: estimateView.object_id,
        }).toString(),
      })
    },
  }),

  isInvoice: trait({
    content_type: 'INVOICE',
    client_id(index: number) {
      return (index + 1).toString()
    },
    invoice_total() {
      const amount = randNumber({ min: 300, max: 33300 }) / 100
      return `€${amount.toFixed(2)}`
    },
    afterCreate(invoiceView) {
      invoiceView.update({
        url: invoiceDetailsUrl({
          provetId: 'PROVET_ID',
          invoiceId: invoiceView.object_id,
        }).toString(),
      })
    },
  }),

  isConsultationLaboratoryReferral: trait({
    content_type: 'CONSULTATION_LABORATORY_REFERRAL',
    client_id(index: number) {
      return (index + 1).toString()
    },
    patient_id(index: number) {
      return (index + 1).toString()
    },
    patient_name() {
      return randFirstName()
    },
    referral_patients_count() {
      return randNumber({ min: 0, max: 3 })
    },
    referral_analyses() {
      return rand([null, randCatchPhrase()])
    },
    afterCreate(referralView) {
      referralView.update({
        url: laboratoryReferralDetailsUrl({
          provetId: 'PROVET_ID',
          referralId: referralView.object_id,
        }).toString(),
      })
    },
  }),

  isDiagnosticImagingReferral: trait({
    content_type: 'DIAGNOSTIC_IMAGING_REFERRAL',
    client_id(index: number) {
      return (index + 1).toString()
    },
    patient_id(index: number) {
      return (index + 1).toString()
    },
    patient_name() {
      return randFirstName()
    },
    referral_patients_count() {
      return randNumber({ min: 0, max: 3 })
    },
    referral_target_areas() {
      return rand([null, randCatchPhrase()])
    },
    afterCreate(referralView) {
      referralView.update({
        url: diagnosticImagingReferralDetailsUrl({
          provetId: 'PROVET_ID',
          referralId: referralView.object_id,
        }).toString(),
      })
    },
  }),
})

export function routes(server: MockServer) {
  server.get(
    '/recently_viewed',
    function (this: MirageServer, schema, request) {
      const searchResults = schema.all('userViewHistory')

      // first we paginate
      const { data, meta } = pageable(searchResults, request)

      // and then we construct the response
      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export async function seeds(server: MockServer) {
  // Generate Clients
  await server.lazySeedingManager?.seedBasic('Client')

  server.createList('userViewHistory', 2)
  server.createList('userViewHistory', 1, 'isPatient')
  server.createList('userViewHistory', 2, 'isConsultation')
  server.createList('userViewHistory', 1, 'isEstimate')
  server.createList('userViewHistory', 1, 'isEstimateWithoutClient')
  server.createList('userViewHistory', 1, 'isInvoice')
  server.createList('userViewHistory', 2, 'isConsultationLaboratoryReferral')
  server.createList('userViewHistory', 2, 'isDiagnosticImagingReferral')
}
